/**
 * Hooks for email verification functionality
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@ui/use-toast';
import {
  verifyEmail,
  checkEmailVerificationStatus,
  resendEmailVerification,
  type MessageResult,
  type VerifyEmailData,
  type ResendVerificationEmailData,
} from '@lib/api/auth';
import { toProblem } from '@lib/api/base';

export interface ResendEmailVerificationOptions {
  suppressToast?: boolean;
}

/**
 * Hook for verifying email with verification key
 */
export const useVerifyEmail = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation<MessageResult, unknown, VerifyEmailData>({
    mutationFn: verifyEmail,
    onSuccess: ({ message }) => {
      // Refresh the user data to update verification status
      queryClient.invalidateQueries({ queryKey: ['currentUser'] });

      toast({
        title: 'Email Verified',
        description: message || 'Your email has been verified successfully.',
      });
    },
    onError: error => {
      const problem = toProblem(error);
      toast({
        title: 'Verification Failed',
        description:
          problem.detail ||
          problem.title ||
          'There was a problem verifying your email. Please try again.',
        variant: 'destructive',
      });
    },
  });
};

/**
 * Hook for checking email verification status
 */
export const useEmailVerificationStatus = () => {
  return useQuery({
    queryKey: ['emailVerificationStatus'],
    queryFn: checkEmailVerificationStatus,
    retry: false,
  });
};

/**
 * Hook for resending email verification
 */
export const useResendEmailVerification = (options?: ResendEmailVerificationOptions) => {
  const { toast } = useToast();
  const suppressToast = options?.suppressToast || false;

  return useMutation<MessageResult, unknown, ResendVerificationEmailData>({
    mutationFn: resendEmailVerification,
    onSuccess: ({ message }) => {
      if (!suppressToast) {
        const description = message || 'Verification email has been sent. Please check your inbox.';
        toast({
          title: 'Email Sent',
          description,
        });
      }
    },
    onError: error => {
      const problem = toProblem(error);
      if (!suppressToast) {
        const description =
          problem.detail ||
          problem.title ||
          'There was a problem sending the verification email. Please try again.';

        toast({
          title: 'Failed to Send Email',
          description,
          variant: 'destructive',
        });
      }
    },
  });
};
